import {
  can,
  type CatalogProduct,
  departmentIdOf,
  PRODUCT_ID,
  type ProductBody,
  ProductBodySchema,
} from '@atlas-control/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { PageHeader } from '@/components/page-header';
import { StatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { guard } from '@/lib/guards';
import { toastError, toastSuccess } from '@/lib/notify';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/catalog/')({
  beforeLoad: ({ context }) => guard(context.me, 'runs.read'),
  component: CatalogPage,
});

const productsQuery = queryOptions({
  queryKey: ['catalog', 'products'],
  queryFn: () =>
    api<{ products: CatalogProduct[] }>('/catalog/products', {
      query: { include_inactive: 'true' },
    }).then((r) => r.products),
});

/** Our products, which Brain proposes first. Each run takes a copy when it starts. */
function CatalogPage() {
  const { user } = useMe();
  const manage = can(user.role, 'catalog.manage');
  const qc = useQueryClient();
  const products = useQuery(productsQuery);
  const [editing, setEditing] = useState<CatalogProduct | 'new' | null>(null);
  const [deleting, setDeleting] = useState<CatalogProduct | null>(null);

  const refresh = () => void qc.invalidateQueries({ queryKey: ['catalog'] });
  const save = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ProductBody }) =>
      api<{ product: CatalogProduct }>(`/catalog/products/${id}`, { method: 'PUT', body }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/catalog/products/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  // Inactive products stay listed (greyed, with their badge) so a deactivation is visible
  // where it happened instead of the row vanishing and the next one taking its place.
  const rows = products.data ?? [];

  return (
    <>
      <PageHeader
        title="Product catalog"
        actions={
          manage && (
            <Button onClick={() => setEditing('new')}>
              <Plus /> Add product
            </Button>
          )
        }
      />
      <Card className="py-0">
        <Table className="min-w-[56rem] table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[34%]">Product</TableHead>
              <TableHead>Features</TableHead>
              <TableHead className="w-28">Status</TableHead>
              {manage && <TableHead className="w-56 text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.isPending && (
              <TableRow>
                <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {products.isError && (
              <TableRow>
                <TableCell colSpan={4} className="py-10 text-center text-danger">
                  The catalog is unavailable (Atlas did not answer).
                </TableCell>
              </TableRow>
            )}
            {products.isSuccess && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                  No products yet.
                </TableCell>
              </TableRow>
            )}
            {rows.map((p) => (
              <TableRow
                key={p.id}
                id={p.id}
                className={cn(
                  'scroll-mt-20 target:bg-info-soft',
                  !p.active && 'bg-muted/30 text-muted-foreground',
                )}
              >
                <TableCell className="py-4 align-top whitespace-normal">
                  <p className={cn('font-medium', p.active && 'text-foreground')}>{p.name}</p>
                  <p className="truncate font-mono text-xs text-muted-foreground" title={p.id}>
                    {p.id}
                  </p>
                  {p.description && (
                    <p className="mt-2 line-clamp-3 text-sm" title={p.description}>
                      {p.description}
                    </p>
                  )}
                  {p.url && /^https?:\/\//.test(p.url) && (
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      title={p.url}
                      className="mt-1 block truncate text-xs text-info underline-offset-4 hover:underline"
                    >
                      {p.url.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                </TableCell>
                <TableCell className="py-4 align-top whitespace-normal">
                  <FeatureList features={p.features} />
                </TableCell>
                <TableCell className="py-4 align-top">
                  <StatusBadge tone={p.active ? 'success' : 'neutral'}>
                    {p.active ? 'Active' : 'Inactive'}
                  </StatusBadge>
                </TableCell>
                {manage && (
                  <TableCell className="py-4 align-top">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>
                        <Pencil /> Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title={
                          p.active
                            ? 'Stop offering it to new runs; its history is kept'
                            : 'Offer it to new runs again'
                        }
                        disabled={save.isPending}
                        onClick={() =>
                          save.mutate(
                            { id: p.id, body: { ...toBody(p), active: !p.active } },
                            {
                              onSuccess: () =>
                                toastSuccess(`${p.name} ${p.active ? 'deactivated' : 'activated'}`),
                              onError: (e) => toastError(e),
                            },
                          )
                        }
                      >
                        {p.active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-danger"
                        aria-label={`Delete ${p.name}`}
                        title="Delete"
                        onClick={() => setDeleting(p)}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {editing && (
        <ProductDialog
          product={editing === 'new' ? null : editing}
          taken={new Set((products.data ?? []).map((p) => p.id))}
          pending={save.isPending}
          onClose={() => setEditing(null)}
          onSave={(id, body) =>
            save.mutate(
              { id, body },
              {
                onSuccess: () => {
                  toastSuccess(`Saved ${body.name}`);
                  setEditing(null);
                },
                onError: (e) => toastError(e),
              },
            )
          }
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete ${deleting?.name ?? ''}?`}
        description={
          <>
            <p>
              It is gone from the catalog for good. The tool archive keeps the tools proposed from
              it, without the link.
            </p>
            <p>To stop offering it but keep its history, deactivate it instead.</p>
          </>
        }
        confirmLabel="Delete"
        destructive
        pending={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toastSuccess(`Deleted ${deleting.name}`);
              setDeleting(null);
            },
            onError: (e) => toastError(e),
          })
        }
      />
    </>
  );
}

const FEATURES_SHOWN = 4;

/** The first few features as chips; the rest behind "+N more" so long lists do not stretch the row. */
function FeatureList({ features }: { features: string[] }) {
  const [open, setOpen] = useState(false);
  if (!features.length) return <span className="text-sm text-muted-foreground">-</span>;
  const shown = open ? features : features.slice(0, FEATURES_SHOWN);
  const hidden = features.length - shown.length;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {shown.map((f) => (
        <li key={f} className="rounded-md border bg-muted/40 px-2 py-0.5 text-xs leading-5">
          {f}
        </li>
      ))}
      {(hidden > 0 || open) && features.length > FEATURES_SHOWN && (
        <li>
          <button
            type="button"
            className="px-1 text-xs leading-6 text-info underline-offset-4 hover:underline"
            onClick={() => setOpen(!open)}
          >
            {open ? 'Show less' : `+${hidden} more`}
          </button>
        </li>
      )}
    </ul>
  );
}

function toBody(p: CatalogProduct): ProductBody {
  return {
    name: p.name,
    description: p.description,
    url: p.url,
    active: p.active,
    features: p.features,
  };
}

function ProductDialog({
  product,
  taken,
  pending,
  onClose,
  onSave,
}: {
  product: CatalogProduct | null;
  taken: Set<string>;
  pending: boolean;
  onClose: () => void;
  onSave: (id: string, body: ProductBody) => void;
}) {
  const [name, setName] = useState(product?.name ?? '');
  const [id, setId] = useState(product?.id ?? '');
  const [idTouched, setIdTouched] = useState(Boolean(product));
  const [description, setDescription] = useState(product?.description ?? '');
  const [url, setUrl] = useState(product?.url ?? '');
  const [active, setActive] = useState(product?.active ?? true);
  const [features, setFeatures] = useState<string[]>(product?.features ?? ['']);

  const productId = idTouched ? id : name.trim() ? `prod-${departmentIdOf(name)}` : '';
  const parsed = ProductBodySchema.safeParse({ name, description, url, active, features });
  const idProblem = !PRODUCT_ID.test(productId)
    ? 'Lowercase words joined by hyphens, like prod-atlas-flows'
    : !product && taken.has(productId)
      ? 'Another product already has this id'
      : null;
  const problems = [
    ...(idProblem ? [idProblem] : []),
    ...new Set(parsed.error?.issues.map((i) => i.message) ?? []),
  ];
  const move = (i: number, to: number) => {
    const next = [...features];
    const [f] = next.splice(i, 1);
    if (f !== undefined) next.splice(to, 0, f);
    setFeatures(next);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !pending && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{product ? `Edit ${product.name}` : 'Add product'}</DialogTitle>
          <DialogDescription>Changes apply to runs started after you save.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="p-name">Name</Label>
            <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="p-id">Id</Label>
            <Input
              id="p-id"
              value={productId}
              disabled={Boolean(product)}
              className="font-mono"
              onChange={(e) => {
                setIdTouched(true);
                setId(e.target.value);
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="p-desc">Description</Label>
            <Textarea
              id="p-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="p-url">URL</Label>
            <Input
              id="p-url"
              type="url"
              placeholder="https://"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={active} onCheckedChange={(v) => setActive(v === true)} /> Active
            (offered to new runs)
          </label>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Features</legend>
            <p className="text-xs text-muted-foreground">
              Say what the product does in the words a pain point would use (“Answers customer
              questions on WhatsApp”, not “Conversational layer v2”): Brain matches on words.
            </p>
            {features.map((f, i) => (
              <div key={i} className="flex gap-1">
                <Input
                  aria-label={`Feature ${i + 1}`}
                  value={f}
                  onChange={(e) =>
                    setFeatures(features.map((x, j) => (j === i ? e.target.value : x)))
                  }
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Move up"
                  disabled={i === 0}
                  onClick={() => move(i, i - 1)}
                >
                  <ArrowUp />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Move down"
                  disabled={i === features.length - 1}
                  onClick={() => move(i, i + 1)}
                >
                  <ArrowDown />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove feature ${i + 1}`}
                  onClick={() => setFeatures(features.filter((_, j) => j !== i))}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              disabled={features.length >= 50}
              onClick={() => setFeatures([...features, ''])}
            >
              <Plus /> Add feature
            </Button>
          </fieldset>
          {problems.length > 0 && (
            <ul className="space-y-1 text-sm text-danger" role="alert">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={problems.length > 0 || pending || !parsed.success}
            onClick={() => parsed.success && onSave(productId, parsed.data)}
          >
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
